import axios from 'axios';
import CONFIG, { getApiUrl } from '../utils/config';
import { getAuthHeader } from './authService';

export interface SystemSyncStatus {
  syncEnabled: boolean;
  success: boolean;
  nextSync: string;
  syncCron: string;
}

export const getSystemSyncStatus = async (token: string): Promise<SystemSyncStatus> => {
  try {
    const response = await axios.get(getApiUrl(CONFIG.ENDPOINTS.CLUBS.SYNC_STATUS), {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    throw new Error('Không thể lấy thông tin trạng thái đồng bộ');
  }
};

export const triggerManualSync = async (token: string): Promise<boolean> => {
  try {
    const response = await axios.post(getApiUrl(CONFIG.ENDPOINTS.CLUBS.TRIGGER_SYNC), {}, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data.success || true;
  } catch (error) {
    throw new Error('Không thể kích hoạt đồng bộ thủ công');
  }
};

// Interface cho kết quả trả về
interface ApiResponse<T> {
  result: string;
  message: string;
  data: T;
}

interface StravaAuthUrlResponse {
  auth_url: string;
}

interface StravaTokenResponse {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
  athleteId: number;
  athleteName: string;
}

// Interface cho thông tin kết nối Strava của người dùng
interface StravaConnectionInfo {
  isConnected: boolean;
  athleteName?: string;
  athleteId?: number;
  expiresAt?: number;
}

// Interface cho thông tin người dùng Strava
interface StravaUserInfo {
  stravaId: number | string;
  athleteName?: string;
  username?: string;
  stravaAccessToken?: string;
  stravaRefreshToken?: string;
  stravaTokenExpiresAt?: number;
}

// Hàm đồng bộ nhân viên từ Strava
export const syncFromStrava = async (): Promise<any> => {
  try {
    const authHeader = getAuthHeader();
    if (!authHeader) {
      throw new Error('Bạn chưa đăng nhập');
    }
    
    const response = await fetch(getApiUrl('/api/club-members/sync-from-strava'), {
      method: 'POST',
      headers: {
        'Authorization': authHeader,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Lỗi đồng bộ dữ liệu từ Strava');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Lỗi khi đồng bộ dữ liệu:', error);
    throw error;
  }
};

// Hàm lấy URL xác thực Strava
export const getStravaAuthUrl = async (): Promise<string> => {
  try {
    const authHeader = getAuthHeader();
    if (!authHeader) {
      throw new Error('Bạn chưa đăng nhập');
    }
    
    const response = await fetch(getApiUrl('/api/auth/strava/auth-url'), {
      method: 'GET',
      headers: {
        'Authorization': authHeader
      }
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Không thể lấy URL xác thực Strava');
    }
    
    const data: ApiResponse<StravaAuthUrlResponse> = await response.json();
    return data.data.auth_url;
  } catch (error) {
    console.error('Lỗi khi lấy URL xác thực Strava:', error);
    throw error;
  }
};

// Hàm đổi code lấy token Strava
export const exchangeStravaToken = async (code: string): Promise<StravaTokenResponse> => {
  try {
    const authHeader = getAuthHeader();
    if (!authHeader) {
      throw new Error('Bạn chưa đăng nhập');
    }
    
    // Đảm bảo encode code trong URL
    const encodedCode = encodeURIComponent(code);
    
    const response = await fetch(getApiUrl(`/api/auth/strava/exchange-token?code=${encodedCode}`), {
      method: 'POST',
      headers: {
        'Authorization': authHeader,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Không thể đổi mã xác thực Strava');
    }
    
    const data: ApiResponse<StravaTokenResponse> = await response.json();
    return data.data;
  } catch (error) {
    console.error('Lỗi khi đổi mã xác thực Strava:', error);
    throw error;
  }
};

// Hàm lấy thông tin Strava của người dùng hiện tại
export const getUserStravaInfo = async (): Promise<StravaUserInfo | null> => {
  try {
    const authHeader = getAuthHeader();
    if (!authHeader) {
      throw new Error('Bạn chưa đăng nhập');
    }
    
    const response = await fetch(getApiUrl('/api/auth/strava/user-info'), {
      method: 'GET',
      headers: {
        'Authorization': authHeader
      }
    });
    
    if (!response.ok) {
      return null;
    }
    
    const data = await response.json();
    
    if (!data.result || data.result !== 'SUCCESS' || !data.data) {
      return null;
    }
    
    return data.data;
  } catch (error) {
    console.error('Lỗi khi lấy thông tin Strava của người dùng:', error);
    return null;
  }
};

// Sửa lại hàm kiểm tra kết nối Strava
export const checkStravaConnection = async (): Promise<boolean> => {
  try {
    const token = getUserToken();
    if (!token) {
      throw new Error('Bạn cần đăng nhập để xem thông tin');
    }
    
    console.log('Đang kiểm tra kết nối với token:', token.substring(0, 15) + '...');
    
    // Sử dụng fetch API thay vì axios để kiểm tra
    const response = await fetch(getApiUrl('/api/stats/users/strava-connection'), {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    console.log('Fetch API response status:', response.status);
    
    if (!response.ok) {
      console.error('API responded with error status:', response.status);
      // Thử ghi log toàn bộ nội dung phản hồi
      try {
        const errorText = await response.text();
        console.error('Response body:', errorText);
      } catch (readError) {
        console.error('Could not read response body');
      }
      throw new Error(`API responded with status: ${response.status}`);
    }
    
    // Thử đọc dữ liệu phản hồi
    try {
      const data = await response.json();
      console.log('Connection check success, response data:', data);
    } catch (parseError) {
      console.warn('Could not parse response as JSON, but connection check passed');
    }
    
    return true;
  } catch (error) {
    console.error('Chi tiết lỗi khi kiểm tra kết nối:', error);
    return false;
  }
};

/**
 * Interface cho thông tin người dùng với trạng thái kết nối Strava
 */
export interface UserStravaConnection {
  userId: number;
  username: string;
  email: string;
  fullName: string;
  avatarUrl: string | null;
  department: string | null;
  stravaConnected: boolean;
  stravaTokenExpireDate: number[] | null;
  lastActivityDate: number[] | null;
  activityCount: number;
}

/**
 * Interface cho kết quả kiểm tra kết nối Strava
 */
export interface StravaConnectionStatus {
  totalUserCount: number;
  connectedUserCount: number;
  disconnectedUserCount: number;
  users: UserStravaConnection[];
}

/**
 * Hàm tiện ích để lấy token xác thực từ nhiều nguồn
 * @returns Token xác thực hoặc null
 */
export const getUserToken = (): string | null => {
  // Danh sách các key có thể chứa token
  const possibleTokenKeys = [
    'jwt_token',                 // Key chuẩn của ứng dụng hiện tại
    'token',                     // Key phổ biến cho token
    'auth_token',                // Key phổ biến khác
    'access_token',              // JWT access token
    'id_token'                   // Key cho loại token ID
  ];
  
  // Thử lấy token từ tất cả các key có thể
  for (const key of possibleTokenKeys) {
    const token = localStorage.getItem(key);
    if (token && token.length > 20) { // Kiểm tra token có vẻ hợp lệ (ít nhất dài hơn 20 ký tự)
      console.log(`Đã tìm thấy token trong localStorage với key: ${key}`);
      return token;
    }
  }
  
  return null;
};

/**
 * Lấy danh sách người dùng và trạng thái kết nối Strava
 * @returns Danh sách người dùng và trạng thái kết nối
 */
export const getUsersStravaConnection = async (): Promise<StravaConnectionStatus> => {
  try {
    const token = getUserToken();
    if (!token) {
      throw new Error('Bạn cần đăng nhập để xem thông tin');
    }

    // In ra token để debug
    console.log('Token being used:', token.substring(0, 15) + '...');
    
    // Gọi API với token trực tiếp
    const url = getApiUrl('/api/stats/users/strava-connection');
    console.log('Calling API URL:', url);
    
    const response = await axios.get<ApiResponse<StravaConnectionStatus>>(
      url,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    return response.data.data;
  } catch (error: any) {
    console.error('Lỗi khi lấy thông tin kết nối Strava:', error);
    console.error('Response status:', error.response?.status);
    console.error('Response data:', error.response?.data);
    throw new Error(error.response?.data?.message || 'Không thể lấy thông tin kết nối Strava');
  }
};

/**
 * Đồng bộ hoạt động của một người dùng từ Strava
 * @param userId ID của người dùng cần đồng bộ
 * @param afterDate Ngày bắt đầu đồng bộ (YYYY-MM-DDT00:00:00)
 * @returns Số lượng hoạt động đã đồng bộ
 */
export const syncUserActivities = async (
  userId: number,
  afterDate: string = '2020-01-01T00:00:00'
): Promise<number> => {
  try {
    const token = getUserToken();
    if (!token) {
      throw new Error('Bạn cần đăng nhập để thực hiện đồng bộ');
    }

    const response = await axios.post<ApiResponse<number>>(
      getApiUrl(`/api/strava/sync/${userId}`),
      { after: afterDate },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    return response.data.data;
  } catch (error: any) {
    console.error(`Lỗi khi đồng bộ hoạt động của người dùng ${userId}:`, error);
    throw new Error(error.response?.data?.message || 'Không thể đồng bộ hoạt động');
  }
};

// Interface cho kết quả đồng bộ tất cả người dùng
export interface SyncAllUsersResponse {
  totalActivitiesSynced: number;
  totalUsers: number;
  durationSeconds: number;
  failedUsers: number;
  startTime: string;
  endTime: string;
  successfulUsers: number;
  userDetails: {
    activitiesSynced: number;
    userName: string;
    message: string;
    userId: number;
    email: string;
    status: string;
  }[];
  errors: string[];
}

export const syncAllUsersActivities = async (): Promise<SyncAllUsersResponse> => {
  try {
    const token = getUserToken();
    if (!token) {
      throw new Error('Bạn cần đăng nhập để thực hiện đồng bộ');
    }

    const response = await axios.get<ApiResponse<SyncAllUsersResponse>>(
      getApiUrl('/api/strava/sync/all'),
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    return response.data.data;
  } catch (error: any) {
    console.error('Lỗi khi đồng bộ hoạt động của tất cả người dùng:', error);
    throw new Error(error.response?.data?.message || 'Không thể đồng bộ hoạt động của tất cả người dùng');
  }
};

// Interface cho lịch trình đồng bộ
export interface SyncScheduleInfo {
  description: string;
  nextExecution: number[];
  schedule: string;
}

// Interface cho kết quả lịch trình đồng bộ tiếp theo
export interface NextSyncSchedulesResponse {
  refreshTokens: SyncScheduleInfo;
  syncActivities: SyncScheduleInfo;
  fullSyncActivities: SyncScheduleInfo;
  checkExpiredTokens: SyncScheduleInfo;
  serverTime: number[];
}

/**
 * Lấy thông tin lịch trình đồng bộ tiếp theo
 * @returns Thông tin lịch trình đồng bộ tiếp theo
 */
export const getNextSyncSchedules = async (): Promise<NextSyncSchedulesResponse> => {
  try {
    const token = getUserToken();
    if (!token) {
      throw new Error('Bạn cần đăng nhập để xem thông tin');
    }

    const response = await axios.get<ApiResponse<NextSyncSchedulesResponse>>(
      getApiUrl('/api/system/next-sync-schedules'),
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    return response.data.data;
  } catch (error: any) {
    console.error('Lỗi khi lấy thông tin lịch trình đồng bộ:', error);
    throw new Error(error.response?.data?.message || 'Không thể lấy thông tin lịch trình đồng bộ');
  }
}; 